<template>
  <!-- prettier-ignore -->
  <div
    v-if="ready"
    class="widget"
  >
    <div class="widget-header">
      <PriceWidget.Header
        :person-id="personId"
        :product-id="productId"
        :source="$props.source"
        @select="selectPerson"
      />
    </div>
    <div
      v-if="personId"
      class="widget-content"
    >
      <PriceWidget.Product
        v-if="!isLocked"
        :key="`${personId}-${$props.productId}`"
        :person-id="personId"
        :product-id="$props.productId"
        :source="$props.source"
      />
    </div>

    <the-form-modal
      v-slot="{ modalProps }"
      v-model="showProductSuggestion"
      open
    >
      <ovp-suggestion
        :category-id="suggestion.categoryId"
        :group-id="suggestion.groupId"
        :doctor="suggestion.doctor"
        :person-id="suggestion.personId"
        :product="suggestion.product"
        :source="suggestion.source"
        @added="modalProps.close"
      />
    </the-form-modal>
  </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, onBeforeUnmount, watchEffect } from 'vue'
import { events$ } from '@/services'

import * as PriceWidget from '@/modules/PriceWidget'

import { OvpProductSuggestion as OvpSuggestion } from '@/components/Product'
import TheFormModal from '@/components/Modal/TheFormModal'

import { CHANNEL } from '@/config/constants'
import { EVENT_OVP, EVENT_SHOW_PRODUCT_SUGGESTION } from '@/config/events'

import { useSession, usePerson, useBasket } from '@/hooks'
import { useI18n } from 'vue-i18n'

// Hooks
const { autostart } = useSession()
const { locale } = useI18n()
const { getPerson, persons } = usePerson()
const { basket } = useBasket()

// Init
defineProps({
  productId: {
    type: String,
    required: true,
  },

  source: {
    type: String,
    required: true,
  },
})

// Data
const personId = ref(null)
const ready = ref(false)
const suggestion = reactive({
  categoryId: null,
  groupId: null,
  doctor: null,
  personId: null,
  product: null,
  source: null,
})
const showProductSuggestion = ref(false)
const language = locale.value.toUpperCase()

// Computed
const isLocked = computed(() => {
  if (!basket.existingCustomer) return false
  const person = getPerson(personId.value)
  return !person.contractModified
})

// Methods
function selectPerson(_personId) {
  if (!_personId) _personId = persons.value[0].personId

  return (personId.value = _personId)
}

function onShowSuggestion(opts) {
  showProductSuggestion.value = true
  // @TODO check usage of reactivity util reassign to delete `suggestion` first. Overwrite vs. new assignment
  Object.assign(suggestion, { ...opts })
}

// Watchers
watchEffect(() => {
  if (personId.value && persons.value.find(p => p.personId === personId.value)) return
  personId.value = persons.value.filter(p => p.contractModified)[0]?.personId ?? null
})

// Lifecycle hooks
onMounted(() => {
  if (!basket.channel) {
    autostart({
      channel: CHANNEL.PRICEWIDGET,
      language,
    }).then(() => (ready.value = true))
  } else {
    ready.value = true
  }

  events$.on(EVENT_SHOW_PRODUCT_SUGGESTION, onShowSuggestion)
  events$.on(EVENT_OVP.BASKET_PREFILLED, selectPerson)
})

onBeforeUnmount(() => {
  events$.off(EVENT_SHOW_PRODUCT_SUGGESTION, onShowSuggestion)
  events$.off(EVENT_OVP.BASKET_PREFILLED, selectPerson)
})
</script>

<style name="global">
.tippy-box .tippy-content ul {
  padding-left: 40px !important;
}

.tippy-box p,
.tippy-box .tippy-content ul li {
  color: var(--c-primary-neutral-3) !important;
}
</style>

<style name="mobile" scoped>
.widget {
  &-header {
    height: auto;
  }

  &-content {
    background-color: var(--c-primary-neutral-3);
    padding: 15px;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .widget {
    &-content {
      padding: 16px;
    }
  }
}
</style>
